export type CompanionFieldMode = "off" | "optional" | "required";

export type CompanionFieldKey =
  | "title"
  | "first_name"
  | "middle_name"
  | "last_name"
  | "company_name"
  | "vat_id"
  | "email"
  | "phone"
  | "license_plate"
  | "age"
  | "date_of_birth"
  | "nationality"
  | "street"
  | "house_number"
  | "address_line_2"
  | "postal_code"
  | "city"
  | "state_region"
  | "country_code";

export type CompanionFieldsConfig = Record<CompanionFieldKey, CompanionFieldMode>;

/** Adressbestandteile, die gemeinsam über „Adresse abfragen“ gesteuert werden. */
export const GUEST_ADDRESS_FIELD_KEYS: CompanionFieldKey[] = [
  "street",
  "house_number",
  "address_line_2",
  "postal_code",
  "city",
  "state_region",
  "country_code",
];

const GUEST_ADDRESS_CORE_KEYS: CompanionFieldKey[] = ["street", "postal_code", "city"];

export function guestAddressInquiryEnabled(fields: CompanionFieldsConfig): boolean {
  return GUEST_ADDRESS_CORE_KEYS.some((key) => fields[key] !== "off");
}

/** Leitet den gemeinsamen Adressmodus aus Straße, PLZ und Ort ab. */
export function getGuestAddressMode(fields: CompanionFieldsConfig): CompanionFieldMode {
  const coreModes = GUEST_ADDRESS_CORE_KEYS.map((key) => fields[key]);
  if (coreModes.every((mode) => mode === "off")) return "off";
  if (coreModes.every((mode) => mode === "required")) return "required";
  return "optional";
}

export function applyGuestAddressMode(
  fields: CompanionFieldsConfig,
  mode: CompanionFieldMode,
): CompanionFieldsConfig {
  const next = { ...fields };
  if (mode === "off") {
    for (const key of GUEST_ADDRESS_FIELD_KEYS) {
      next[key] = "off";
    }
    return next;
  }
  for (const key of GUEST_ADDRESS_CORE_KEYS) {
    next[key] = mode;
  }
  next.house_number = "optional";
  next.address_line_2 = "off";
  next.state_region = "off";
  next.country_code = "optional";
  return next;
}

export function applyGuestAddressInquiry(
  fields: CompanionFieldsConfig,
  enabled: boolean,
): CompanionFieldsConfig {
  return applyGuestAddressMode(fields, enabled ? "required" : "off");
}

/** Ergänzt Hausnummer und Land, wenn die Adressabfrage aktiv ist. */
export function normalizeGuestAddressFieldModes(
  fields: CompanionFieldsConfig,
): CompanionFieldsConfig {
  if (!guestAddressInquiryEnabled(fields)) return fields;
  const next = { ...fields };
  if (next.house_number === "off") next.house_number = "optional";
  if (next.country_code === "off") next.country_code = "optional";
  return next;
}

export const COMPANION_FIELD_ROWS: { key: CompanionFieldKey; label: string }[] = [
  { key: "title", label: "Anrede" },
  { key: "first_name", label: "Vorname" },
  { key: "middle_name", label: "Zweiter Vorname" },
  { key: "last_name", label: "Nachname" },
  { key: "company_name", label: "Firmenname" },
  { key: "vat_id", label: "Umsatzsteuer-ID" },
  { key: "email", label: "E-Mail" },
  { key: "phone", label: "Telefon" },
  { key: "license_plate", label: "Kennzeichen" },
  { key: "age", label: "Alter" },
  { key: "date_of_birth", label: "Geburtsdatum" },
  { key: "nationality", label: "Nationalität" },
  { key: "street", label: "Straße" },
  { key: "house_number", label: "Hausnummer" },
  { key: "address_line_2", label: "Adresszusatz" },
  { key: "postal_code", label: "PLZ" },
  { key: "city", label: "Ort" },
  { key: "state_region", label: "Bundesland/Kanton" },
  { key: "country_code", label: "Land (ISO)" },
];

/** Felder ohne Adressbestandteile (Adresse wird separat konfiguriert). */
export const COMPANION_FIELD_ROWS_WITHOUT_ADDRESS = COMPANION_FIELD_ROWS.filter(
  (row) => !GUEST_ADDRESS_FIELD_KEYS.includes(row.key),
);

export const DEFAULT_COMPANION_FIELDS: CompanionFieldsConfig = {
  title: "off",
  first_name: "required",
  last_name: "required",
  middle_name: "off",
  company_name: "off",
  vat_id: "off",
  email: "off",
  phone: "off",
  license_plate: "off",
  age: "off",
  date_of_birth: "off",
  nationality: "off",
  street: "off",
  house_number: "off",
  address_line_2: "off",
  postal_code: "off",
  city: "off",
  state_region: "off",
  country_code: "off",
};

export const DEFAULT_PRIMARY_GUEST_FIELDS: CompanionFieldsConfig = {
  title: "required",
  first_name: "required",
  middle_name: "off",
  last_name: "required",
  company_name: "off",
  vat_id: "off",
  email: "required",
  phone: "optional",
  license_plate: "optional",
  age: "off",
  date_of_birth: "off",
  nationality: "off",
  street: "off",
  house_number: "off",
  address_line_2: "off",
  postal_code: "off",
  city: "off",
  state_region: "off",
  country_code: "off",
};
